import { PureComponent } from 'react';
import { Text, View } from 'react-native';

import FeatureFlags from '../../../constants/FeatureFlags';
import withShowToast from '../../../decorators/withShowToast/withShowToast';
import Image from '../../../elements/Image';
import ItemLabel from '../../../elements/ItemLabel';
import Price from '../../../elements/Price';
import Touchable from '../../../elements/Touchable';
import { translate } from '../../../i18n';
import Currency from '../../../types/Currency';
import { Product } from '../../../types/TransactionDetail';
import { WebPropsInterface } from '../../../types/WebPropsInterface';
import formatSkuPattern from '../../../utils/formatSkuPattern';
import * as navigationService from '../../../utils/navigationService';
import routeUtils from '../../../utils/routes';
import styles, { S } from './ProductItem.style';

interface Props {
  item: Product;
  isClickable: boolean;
  currency: Currency;
}

class ProductItem extends PureComponent<Props> {
  static defaultProps = {
    currency: {
      symbol: 'NT$',
      iso_code: 'TWD',
    },
  };

  createWebProps = (): Partial<WebPropsInterface> => {
    const { item } = this.props;
    const { id } = item;

    return {
      accessibilityRole: 'link',
      href: routeUtils.compileToPath('ItemPage', { id }),
    };
  };

  handleOnPress = () => {
    const { item, isClickable } = this.props;
    const id = item.id;

    if (isClickable) {
      navigationService.navigate('ItemPage', {
        id,
      });
    }
  };

  render() {
    const { item, currency, isClickable } = this.props;
    const isRefundItem = !!item.refund_status_text;
    const webProps = this.createWebProps();
    const Container = isClickable ? Touchable : View;
    const containerProps = isClickable
      ? {
          onPress: this.handleOnPress,
          ...webProps,
        }
      : {};

    return (
      <Container
        testID="ProductItemContainer"
        style={styles.container}
        {...containerProps}
      >
        <View style={styles.imageSection}>
          <Image
            style={styles.image}
            source={{
              uri: item.sku_image_url || item.image_url,
            }}
          />
        </View>
        <View style={styles.detail}>
          {isRefundItem && (
            <S.RefundLabel key="refundStatusLabel">
              <S.RefundLabelText>{item.refund_status_text}</S.RefundLabelText>
            </S.RefundLabel>
          )}
          <Text
            key="title"
            style={[styles.title, isRefundItem && styles.inverseGreyness]}
            numberOfLines={2}
            ellipsizeMode="tail"
          >
            {item.title}
          </Text>
          <Text
            key="sku"
            style={[styles.sku, isRefundItem && styles.skuWithGreyness]}
          >
            {formatSkuPattern(item.color_text, item.size_text)}
          </Text>
          {item.place_of_shipment && (
            <View style={styles.labelsContainer}>
              <View style={styles.placeOfShipment}>
                <Text
                  key="place_of_shipment"
                  style={styles.placeOfShipmentText}
                >
                  {item.place_of_shipment}
                </Text>
              </View>
            </View>
          )}
          {FeatureFlags.GROWTH_715_OVERSTOCK_CONTAINER && (
            <ItemLabel
              text={translate('fast-delivery-text')}
              itemLabelStyle={{ marginTop: 4 }}
            />
          )}
          <View style={styles.meta}>
            <Text key="amount" style={[styles.sku, styles.amount]}>
              x{item.amount}
            </Text>
            <Price
              price={item.price}
              currencyStyle={[
                styles.price,
                isRefundItem && styles.inverseGreyness,
              ]}
              priceStyle={[
                styles.price,
                isRefundItem && styles.inverseGreyness,
              ]}
              currencySymbol={currency.symbol}
            />
          </View>
        </View>
      </Container>
    );
  }
}

export default withShowToast(ProductItem);
